/* eslint-disable no-restricted-globals */

/** Alternative implementation of https://github.com/MichalZalecki/storage-factory
 * This avoid blocking the main thread and sending a storage event every time we retrieve an item.
 * Instead, check the support once and move on.
 */
const storageFactory = (getStorage: () => Storage) => {
  let inMemoryStorage: { [key: string]: string } = {};
  let isSupported = true;

  try {
    const testKey = '__some_random_key_you_are_not_going_to_use__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    isSupported = true;
  } catch {
    isSupported = false;
  }

  const clear = () => {
    if (isSupported) {
      getStorage().clear();
    } else {
      inMemoryStorage = {};
    }
  };

  const getItem = (name: string) => {
    const stringValue = isSupported
      ? getStorage().getItem(name)
      : inMemoryStorage[name];
    if (stringValue) {
      try {
        return JSON.parse(stringValue);
      } catch {
        return stringValue;
      }
    }
    return null;
  };

  const key = (index: number) => {
    if (isSupported) {
      return getStorage().key(index);
    }
    return Object.keys(inMemoryStorage)[index] || null;
  };

  const removeItem = (name: string) => {
    if (isSupported) {
      getStorage().removeItem(name);
    } else {
      delete inMemoryStorage[name];
    }
  };

  const setItem = (name: string, value: any) => {
    if (isSupported) {
      getStorage().setItem(name, JSON.stringify(value));
    } else {
      inMemoryStorage[name] = value; // not everyone uses TypeScript
    }
  };

  const length = () => {
    if (isSupported) {
      return getStorage().length;
    }
    return Object.keys(inMemoryStorage).length;
  };

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    key,
    get length() {
      return length();
    },
  };
};

export const local = storageFactory(() => localStorage);
// eslint-disable-next-line no-restricted-globals
export const session = storageFactory(() => sessionStorage);
